import React, { useState } from "react";
import { NavLink, Link, useLocation, useParams } from "react-router-dom";
import { Menu, X, ShieldCheck } from "lucide-react";
import { useI18n } from "@/i18n";

const NAV_ITEMS = [
  { key: "navHome", to: "" },
  { key: "navBenefits", hash: "#benefits" },
  { key: "navHowItWorks", hash: "#how-it-works" },
  { key: "navTestimonials", hash: "#testimonials" },
  { key: "navFaq", hash: "#faq" },
];

export default function Header() {
  const { t, lang } = useI18n();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);

  const otherLang = lang === "mx" ? "en" : "mx";
  // Preserve current sub-path but switch language segment
  const parts = pathname.split("/").filter(Boolean);
  if (parts[0] === "mx" || parts[0] === "en") parts.shift();
  const otherLangPath = "/" + [otherLang, ...parts].join("/");

  const basePath = `/${lang}`;
  const buildHref = (item) => (item.hash ? `${basePath}${item.hash}` : basePath);

  return (
    <header
      className="sticky top-0 z-40 bg-[#0B2545] text-white border-b border-[#1a3866]"
      data-testid="site-header"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 md:h-20 items-center justify-between">
          {/* Logo */}
          <Link
            to={basePath}
            data-testid="header-logo"
            className="flex items-center gap-3"
            aria-label={t("header.logoAlt")}
          >
            <span className="grid place-items-center h-10 w-10 rounded-md bg-[#006837] text-white">
              <ShieldCheck className="h-6 w-6" aria-hidden="true" />
            </span>
            <span className="flex flex-col leading-tight">
              <span className="font-serif-heading text-lg font-bold tracking-tight">
                {t("common.companyName")}
              </span>
              <span className="text-[11px] uppercase tracking-widest text-[#8DA9C4]">
                {t("common.brandTagline")}
              </span>
            </span>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-7" data-testid="header-nav-desktop" aria-label="Primary">
            {NAV_ITEMS.map((item) => (
              <a
                key={item.key}
                href={buildHref(item)}
                data-testid={`nav-desktop-${item.key}`}
                className="text-sm font-semibold text-white/90 hover:text-white"
              >
                {t(`header.${item.key}`)}
              </a>
            ))}
          </nav>

          {/* Right cluster */}
          <div className="hidden lg:flex items-center gap-3">
            <Link
              to={otherLangPath}
              data-testid={`language-switch-${otherLang}`}
              className="inline-flex items-center gap-2 rounded-md border border-white/25 px-3 py-2 text-xs font-semibold uppercase tracking-wider text-white hover:bg-white/10"
              aria-label={t("common.language")}
            >
              {otherLang === "mx" ? "ES · MX" : "EN · US"}
            </Link>
            <a
              href={`${basePath}#newsletter`}
              data-testid="header-cta-consultation"
              className="inline-flex items-center rounded-md bg-[#006837] hover:bg-[#00512b] px-4 py-2 text-sm font-semibold text-white shadow"
            >
              {t("header.ctaConsultation")}
            </a>
          </div>

          {/* Mobile toggle */}
          <button
            type="button"
            className="lg:hidden inline-flex items-center justify-center h-11 w-11 rounded-md border border-white/25 text-white"
            onClick={() => setOpen((v) => !v)}
            aria-label={open ? t("common.closeMenu") : t("common.openMenu")}
            aria-expanded={open}
            data-testid="mobile-menu-button"
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile Nav */}
        {open && (
          <div className="lg:hidden pb-4" data-testid="mobile-menu-panel">
            <nav className="flex flex-col gap-1" aria-label="Mobile primary">
              {NAV_ITEMS.map((item) => (
                <a
                  key={item.key}
                  href={buildHref(item)}
                  onClick={() => setOpen(false)}
                  data-testid={`nav-mobile-${item.key}`}
                  className="rounded-md px-3 py-3 text-base font-semibold text-white/95 hover:bg-white/10"
                >
                  {t(`header.${item.key}`)}
                </a>
              ))}
              <div className="mt-3 flex items-center gap-3">
                <Link
                  to={otherLangPath}
                  onClick={() => setOpen(false)}
                  data-testid={`language-switch-mobile-${otherLang}`}
                  className="inline-flex items-center gap-2 rounded-md border border-white/25 px-3 py-2 text-xs font-semibold uppercase tracking-wider text-white"
                >
                  {otherLang === "mx" ? "ES · MX" : "EN · US"}
                </Link>
                <a
                  href={`${basePath}#newsletter`}
                  onClick={() => setOpen(false)}
                  data-testid="header-cta-consultation-mobile"
                  className="inline-flex items-center rounded-md bg-[#006837] hover:bg-[#00512b] px-4 py-2 text-sm font-semibold text-white shadow"
                >
                  {t("header.ctaConsultation")}
                </a>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}
